/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react'
import {
  Button, DeleteWithConfirmButton, EditButton,
  Show, TabbedShowLayout,
  TopToolbar, useDataProvider, useRecordContext, useRedirect, useGetList,
  useTranslate, useRefresh, useNotify, Tab,
} from 'react-admin'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LinkIcon from '@mui/icons-material/Link'
import StorageIcon from '@mui/icons-material/Storage'
import SyncIcon from '@mui/icons-material/Sync'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  Alert, Box, Button as MuiButton, Card, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Pagination as MuiPagination,
  Switch, TextField as MuiTextField, MenuItem, Typography, alpha,
} from '@mui/material'
import {
  CopyBlock, DetailCard, InfoRow, LOG_METHOD_COLORS, LogBodyPreview, LogsChart,
  StatCard, McpLinksDatagrid, SearchField, StatusChip, SyncButton,
  ToolSourceChip, formatDateTime,
} from '../../components'
import { xxllncColor } from '../../layout/theme/color'
import { fadeSlideIn } from '../../components/dashboard'
import {
  ApplicationOut, McpServerOut, McpServerSkillOut, McpToolOut,
  OrganizationMcpLinkOut, ToolSourceOut, ToolSourceSyncResult,
} from '../../types/apiRequests'
import { McpSkillOut, McpServerSkillLink, McpUsageLogOut } from '../../types/localTypes'
import { matchesSearch } from '../../utils/matchesSearch'
import { getMcpBaseUrl, getUrl, httpClient, postJson } from '../../providers/data'
import ToolEditDialog from './ToolEditDialog'

const BLUE = xxllncColor.dark100
const PAGE_SIZE = 100

const useGetAllTools = (filter: Record<string, unknown>, enabled: boolean) => {
  const dataProvider = useDataProvider()
  const [data, setData] = useState<McpToolOut[]>([])
  const [loading, setLoading] = useState(false)
  const [version, setVersion] = useState(0)
  // Stable serialised filter for the effect dep array (avoids JSON.stringify per render).
  const filterKey = Object.entries(filter).map(([k, v]) => `${k}=${String(v)}`).join('&')

  useEffect(() => {
    if (!enabled) { setData([]); return }
    let cancelled = false
    setLoading(true)

    const fetchAll = async () => {
      const all: McpToolOut[] = []
      let page = 1
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data: pageData, total } = await dataProvider.getList<McpToolOut>('mcp_tool', {
          pagination: { page, perPage: PAGE_SIZE },
          sort: { field: 'name', order: 'ASC' },
          filter,
        })
        if (cancelled) return
        all.push(...pageData)
        if (!total || all.length >= total) break
        page++
      }
      if (!cancelled) { setData(all); setLoading(false) }
    }
    void fetchAll().catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [enabled, filterKey, version])

  const updateTool = (toolId: string, updates: Partial<McpToolOut>) => {
    setData((prev) => prev.map((t) => (t.id === toolId ? { ...t, ...updates } : t)))
  }

  return { data, loading, refetch: () => setVersion((v) => v + 1), updateTool }
}

const ShowActions: FC = () => {
  const translate = useTranslate()
  return (
    <TopToolbar>
      <EditButton />
      <DeleteWithConfirmButton
        confirmTitle={translate('delete.confirmTitle')}
        confirmContent={translate('delete.confirmContentMcp')}
      />
    </TopToolbar>
  )
}

const AddSourceDialog: FC<{
  open: boolean
  serverId?: string
  onClose: () => void
  onAdded: () => void
}> = ({ open, serverId, onClose, onAdded }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [name, setName] = useState('')
  const [sourceType, setSourceType] = useState('openapi')
  const [specUrl, setSpecUrl] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (!name || !specUrl || !serverId) return
    setLoading(true)
    try {
      await postJson(`${getUrl()}/tool_source`, {
        // eslint-disable-next-line camelcase
        server_id: serverId,
        name,
        // eslint-disable-next-line camelcase
        display_name: name,
        // eslint-disable-next-line camelcase
        source_type: sourceType,
        // eslint-disable-next-line camelcase
        spec_url: specUrl,
      })
      notify(translate('data.addSourceSuccess'), { type: 'success' })
      setName('')
      setSpecUrl('')
      onClose()
      onAdded()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('data.addSource')}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        <MuiTextField
          label={translate('data.addSourceName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          size="small"
        />
        <MuiTextField
          label={translate('data.addSourceType')}
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value)}
          fullWidth
          select
          size="small"
        >
          <MenuItem value="openapi">OpenAPI</MenuItem>
          <MenuItem value="odata">OData</MenuItem>
        </MuiTextField>
        <MuiTextField
          label={translate('data.addSourceSpecUrl')}
          value={specUrl}
          onChange={(e) => setSpecUrl(e.target.value)}
          fullWidth
          required
          size="small"
          helperText={translate('data.addSourceSpecUrlHelp')}
        />
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>{translate('ra.action.cancel')}</MuiButton>
        <MuiButton onClick={() => void handleSubmit()} disabled={!name || !specUrl || loading} variant="contained">
          {loading ? <CircularProgress size={18} /> : translate('data.addSourceSubmit')}
        </MuiButton>
      </DialogActions>
    </Dialog>
  )
}

const METHOD_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  GET: { bg: 'rgba(49,103,155,0.06)', border: 'rgba(49,103,155,0.25)', text: '#31679B' },
  POST: { bg: 'rgba(0,115,77,0.06)', border: 'rgba(0,115,77,0.25)', text: '#00734D' },
  PUT: { bg: 'rgba(232,110,68,0.06)', border: 'rgba(232,110,68,0.25)', text: '#E86E44' },
  DELETE: { bg: 'rgba(194,66,66,0.06)', border: 'rgba(194,66,66,0.25)', text: '#C24242' },
  PATCH: { bg: 'rgba(142,68,173,0.06)', border: 'rgba(142,68,173,0.25)', text: '#8E44AD' },
}

const ToolRow: FC<{
  tool: McpToolOut
  onClick: () => void
  onToggle?: (id: string, enabled: boolean) => void
}> = ({ tool, onClick, onToggle }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider()
  const mc = METHOD_COLORS[tool.http_method] ?? METHOD_COLORS.GET

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newEnabled = !tool.enabled
    if (onToggle) onToggle(tool.id, newEnabled)
    void dataProvider.update('mcp_tool', { id: tool.id, data: { enabled: newEnabled }, previousData: tool })
      .catch(() => {
        if (onToggle) onToggle(tool.id, !newEnabled)
        notify('ra.notification.http_error', { type: 'error' })
      })
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        'display': 'flex', 'alignItems': 'center', 'gap': 1.5, 'px': 2, 'py': 1,
        'borderRadius': '8px', 'border': `1px solid ${mc.border}`, 'bgcolor': mc.bg,
        'cursor': 'pointer', 'transition': 'all 0.15s',
        'opacity': tool.enabled ? 1 : 0.4,
        '&:hover': { bgcolor: mc.border, borderColor: mc.text },
      }}
    >
      <Box
        sx={{
          minWidth: 56,
          textAlign: 'center',
          px: 1,
          py: 0.25,
          borderRadius: '4px',
          bgcolor: mc.text,
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 800,
          letterSpacing: 0.5,
        }}
      >
        {tool.http_method}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          minWidth: 200,
        }}
      >
        {tool.path}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, fontSize: '0.8rem' }} noWrap>
        {tool.description}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
        {tool.is_modified && (
          <Chip
            icon={<EditNoteIcon sx={{ fontSize: 12 }} />}
            label={translate('data.modified')}
            size="small"
            sx={{
              'height': 18,
              'fontSize': '0.6rem',
              'fontWeight': 600,
              'bgcolor': alpha(BLUE, 0.08),
              'color': BLUE,
              'borderRadius': '4px',
              '& .MuiChip-icon': { color: BLUE, ml: 0.3 },
            }}
          />
        )}
        <Switch
          checked={tool.enabled}
          onClick={handleToggle}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: xxllncColor.green100 },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: xxllncColor.opGreen60,
            },
          }}
        />
      </Box>
    </Box>
  )
}

const TagSection: FC<{
  tag: string
  tools: McpToolOut[]
  onEditTool: (tool: McpToolOut) => void
  onToggle?: (id: string, enabled: boolean) => void
  storageKey: string
}> = ({ tag, tools, onEditTool, onToggle, storageKey }) => {
  const translate = useTranslate()
  const lsKey = `sa_tag_${storageKey}_${tag}`
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(lsKey) === 'collapsed')

  const toggle = () => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem(lsKey, next ? 'collapsed' : 'open')
  }

  const enabledCount = tools.filter((t) => t.enabled).length

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box onClick={toggle} sx={{
        'display': 'flex', 'alignItems': 'center', 'gap': 1, 'py': 0.75, 'px': 1,
        'cursor': 'pointer', 'borderRadius': '6px', 'userSelect': 'none',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
      }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'rgba(0,0,0,0.5)',
          }}
        >
          {collapsed ? '▸' : '▾'} {tag || translate('data.other')}
        </Typography>
        <Chip
          label={`${enabledCount} / ${tools.length}`}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            fontWeight: 600,
            bgcolor: 'rgba(0,0,0,0.04)',
            borderRadius: '4px',
          }}
        />
      </Box>
      {!collapsed && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          {tools.map((tool) => (
            <ToolRow key={tool.id} tool={tool} onClick={() => onEditTool(tool)} onToggle={onToggle} />
          ))}
        </Box>
      )}
    </Box>
  )
}

const SourceSection: FC<{
  source: ToolSourceOut
  serverId: string
  onEditTool: (tool: McpToolOut) => void
  onRefresh: () => void
}> = ({ source, serverId, onEditTool, onRefresh }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider()
  const [syncing, setSyncing] = useState(false)
  const [search, setSearch] = useState('')

  const { data: tools, refetch: refetchTools, updateTool } = useGetAllTools(
    {
      // eslint-disable-next-line camelcase
      source_id: source.id,
    },
    !!source.id
  )

  const lsHideKey = `sa_hide_disabled_${serverId}_${source.id}`
  const [hideDisabled, setHideDisabled] = useState(() => localStorage.getItem(lsHideKey) === 'true')

  const toggleHideDisabled = () => {
    const next = !hideDisabled
    setHideDisabled(next)
    localStorage.setItem(lsHideKey, next ? 'true' : 'false')
  }

  const enabledTools = tools.filter((t) => t.enabled)
  const disabledCount = tools.length - enabledTools.length
  const visibleTools = (hideDisabled ? enabledTools : tools).filter((row) => {
    if (!search) return true
    // eslint-disable-next-line camelcase
    return matchesSearch([row.name, row.description, row.path, row.http_method, row.tag], search.toLowerCase())
  })

  const tagGroups: { tag: string; tools: McpToolOut[] }[] = []
  const tagMap = new Map<string, McpToolOut[]>()
  for (const tool of visibleTools) {
    const tag = tool.tag || ''
    if (!tagMap.has(tag)) tagMap.set(tag, [])
    tagMap.get(tag)!.push(tool)
  }
  tagMap.forEach((tagTools, tag) => {
    tagGroups.push({ tag, tools: tagTools })
  })
  const hasTags = tagGroups.length > 1 || (tagGroups.length === 1 && tagGroups[0].tag !== '')

  const handleSync = async () => {
    setSyncing(true)
    try {
      const result = await postJson<ToolSourceSyncResult>(
        `${getUrl()}/tool_source/${source.id}/sync`
      )
      notify(
        translate('data.syncResult', {
          created: result.tools_created,
          updated: result.tools_updated,
          disabled: result.tools_disabled,
        }),
        { type: 'success' }
      )
      refetchTools()
      onRefresh()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setSyncing(false)
    }
  }

  const handleDelete = async () => {
    try {
      await httpClient(`${getUrl()}/tool_source/${source.id}`, { method: 'DELETE' })
      notify(translate('data.sourceDeleted'), { type: 'info' })
      onRefresh()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    }
  }

  const handleToolToggle = (id: string, enabled: boolean) => {
    updateTool(id, { enabled })
  }

  const handleToggleAll = (enabled: boolean) => {
    const toUpdate = tools.filter((t) => t.enabled !== enabled)
    toUpdate.forEach((t) => updateTool(t.id, { enabled }))
    void Promise.all(
      toUpdate.map((t) => dataProvider.update('mcp_tool', { id: t.id, data: { enabled }, previousData: t }))
    ).then(() => { refetchTools(); onRefresh() })
      .catch(() => { refetchTools(); notify('ra.notification.http_error', { type: 'error' }) })
  }

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          bgcolor: 'rgba(0,0,0,0.015)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                bgcolor: alpha(BLUE, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StorageIcon sx={{ fontSize: 16, color: BLUE }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {source.display_name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
              >
                {source.spec_url}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={source.source_type.toUpperCase()}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: alpha(BLUE, 0.08),
                color: BLUE,
                borderRadius: '6px',
              }}
            />
            <Chip
              label={`${enabledTools.length} / ${tools.length}`}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'rgba(0,0,0,0.04)',
                borderRadius: '6px',
              }}
            />
            <MuiButton
              size="small"
              startIcon={
                syncing ? (
                  <CircularProgress size={14} />
                ) : (
                  <SyncIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={() => void handleSync()}
              disabled={syncing}
              sx={{
                'textTransform': 'none',
                'fontWeight': 600,
                'fontSize': 12,
                'minWidth': 0,
                'px': 1.5,
                'borderRadius': '8px',
                '&:hover': { bgcolor: alpha(BLUE, 0.08) },
              }}
            >
              {translate('data.syncTools')}
            </MuiButton>
            <MuiButton
              size="small"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={() => void handleDelete()}
              color="error"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                minWidth: 0,
                px: 1.5,
                borderRadius: '8px',
              }}
            >
              {translate('data.deleteSource')}
            </MuiButton>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid rgba(0,0,0,0.04)',
        }}
      >
        <SearchField value={search} onChange={setSearch} />
        <MuiButton
          size="small"
          onClick={() => handleToggleAll(true)}
          sx={{
            'textTransform': 'none',
            'fontWeight': 600,
            'fontSize': 11,
            'borderRadius': '8px',
            'px': 1.5,
            'color': 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
          disabled={enabledTools.length === tools.length}
        >
          {translate('data.enableAll')}
        </MuiButton>
        <MuiButton
          size="small"
          onClick={() => handleToggleAll(false)}
          sx={{
            'textTransform': 'none',
            'fontWeight': 600,
            'fontSize': 11,
            'borderRadius': '8px',
            'px': 1.5,
            'color': 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
          disabled={enabledTools.length === 0}
        >
          {translate('data.disableAll')}
        </MuiButton>
        {disabledCount > 0 && (
          <MuiButton
            size="small"
            variant={hideDisabled ? 'contained' : 'text'}
            startIcon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
            onClick={toggleHideDisabled}
            sx={{
              'textTransform': 'none',
              'fontWeight': 600,
              'fontSize': 11,
              'borderRadius': '8px',
              'px': 1.5,
              'color': hideDisabled ? '#fff' : 'rgba(0,0,0,0.4)',
              'bgcolor': hideDisabled ? 'rgba(0,0,0,0.5)' : 'transparent',
              '&:hover': {
                bgcolor: hideDisabled ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            {translate('data.hideDisabled', { count: disabledCount })}
          </MuiButton>
        )}
      </Box>
      <Box sx={{ px: 3, py: 2 }}>
        {hasTags ? (
          tagGroups.map(({ tag, tools: tagTools }) => (
            <TagSection
              key={tag}
              tag={tag}
              tools={tagTools}
              onEditTool={onEditTool}
              onToggle={handleToolToggle}
              storageKey={`${serverId}_${source.id}`}
            />
          ))
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {visibleTools.map((tool) => (
              <ToolRow
                key={tool.id}
                tool={tool}
                onClick={() => onEditTool(tool)}
                onToggle={handleToolToggle}
              />
            ))}
          </Box>
        )}
        {visibleTools.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {search ? translate('data.noToolsFound') : translate('data.noTools')}
          </Typography>
        )}
      </Box>
    </Card>
  )
}

const ToolsTab: FC = () => {
  const record = useRecordContext<McpServerOut>()
  const redirect = useRedirect()
  const translate = useTranslate()
  const refresh = useRefresh()
  const [editTool, setEditTool] = useState<McpToolOut | null>(null)
  const [addSourceOpen, setAddSourceOpen] = useState(false)

  const { data: sources, isLoading: sourcesLoading } = useGetList<ToolSourceOut>(
    'tool_source',
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { server_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const { data: manualToolsData } = useGetList<McpToolOut>(
    'mcp_tool',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { server_id: record?.id },
    },
    { enabled: !!record?.id && record?.tool_source === 'manual' }
  )

  const allSources = sources ?? []
  const hasSources = allSources.length > 0
  const manualTools = (manualToolsData ?? []).filter((t) => !t.source_name)

  return (
    <>
      {!hasSources && record?.tool_source !== 'manual' && (
        <Box sx={{ mb: 2 }}>
          <SyncButton resource="mcp_server" />
        </Box>
      )}
      {allSources.map((source) => (
        <SourceSection
          key={source.id}
          source={source}
          serverId={record?.id as string}
          onEditTool={setEditTool}
          onRefresh={refresh}
        />
      ))}
      {!hasSources && !sourcesLoading && record?.openapi_url && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {record.tool_source === 'odata'
            ? translate('data.odataUrl')
            : translate('data.openapiSource')}
          : {record.openapi_url}
        </Alert>
      )}
      {manualTools.length > 0 && (
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 2,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              bgcolor: 'rgba(0,0,0,0.015)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: 'rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EditNoteIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.5)' }} />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700 }}
                >
                  {translate('data.manualTools')}
                </Typography>
                <Chip
                  label={`${manualTools.length}`}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(0,0,0,0.04)',
                    borderRadius: '6px',
                  }}
                />
              </Box>
              <Button
                label="resources.mcp_tool.create"
                onClick={() =>
                  redirect('create', 'mcp_tool', undefined, undefined, {
                    serverId: record?.id,
                  })
                }
              >
                <AddIcon />
              </Button>
            </Box>
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {manualTools.map((tool) => (
              <ToolRow
                key={tool.id}
                tool={tool}
                onClick={() => setEditTool(tool)}
              />
            ))}
          </Box>
        </Card>
      )}
      {record?.tool_source === 'manual' && manualTools.length === 0 && !hasSources && (
        <Button
          label="resources.mcp_tool.create"
          onClick={() =>
            redirect('create', 'mcp_tool', undefined, undefined, {
              serverId: record?.id,
            })
          }>
          <AddIcon />
        </Button>
      )}
      <MuiButton
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => setAddSourceOpen(true)}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          mt: 1,
          borderStyle: 'dashed',
          borderColor: 'rgba(0,0,0,0.2)',
          color: 'rgba(0,0,0,0.5)',
        }}
      >
        {translate('data.addSource')}
      </MuiButton>

      <AddSourceDialog
        open={addSourceOpen}
        serverId={record?.id}
        onClose={() => setAddSourceOpen(false)}
        onAdded={refresh}
      />
      <ToolEditDialog tool={editTool} open={!!editTool} onClose={() => setEditTool(null)} onSaved={refresh} />
    </>
  )
}


const LogDetailPanel: FC<{ log: McpUsageLogOut }> = ({ log }) => (
  <Box sx={{ px: 3, py: 2, bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 2,
      mb: log.response_body_preview ? 2 : 0,
    }}>
      {log.response_status_code && (
        <Box>
          <Typography variant="caption" color="text.secondary">Status</Typography>
          <Typography variant="body2" fontWeight={600} color={log.is_success ? 'success.main' : 'error.main'}>
            {log.response_status_code}
          </Typography>
        </Box>
      )}
      {log.request_url && (
        <Box>
          <Typography variant="caption" color="text.secondary">URL</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
            {log.request_url}
          </Typography>
        </Box>
      )}
      {log.response_time_ms != null && (
        <Box>
          <Typography variant="caption" color="text.secondary">Response time</Typography>
          <Typography variant="body2" fontWeight={600}>{log.response_time_ms}ms</Typography>
        </Box>
      )}
      {log.error_message && (
        <Box>
          <Typography variant="caption" color="text.secondary">Error</Typography>
          <Typography variant="body2" color="error.main">{log.error_message}</Typography>
        </Box>
      )}
    </Box>
    {log.response_body_preview && (
      <LogBodyPreview preview={log.response_body_preview} />
    )}
  </Box>
)

const LogRow: FC<{ log: McpUsageLogOut }> = ({ log }) => {
  const [expanded, setExpanded] = useState(false)
  const hasDetails = !log.is_success || !!log.request_url || !!log.response_body_preview
  const methodColor = LOG_METHOD_COLORS[log.http_method] ?? 'rgba(0,0,0,0.45)'

  return (
    <Box
      sx={{
        'borderBottom': '1px solid rgba(0,0,0,0.06)',
        'bgcolor': !log.is_success ? 'rgba(194,66,66,0.04)' : 'transparent',
        '&:hover': { bgcolor: !log.is_success ? 'rgba(194,66,66,0.08)' : 'rgba(0,0,0,0.02)' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr 1fr 1fr 80px 70px 60px 32px',
          alignItems: 'center',
          px: 2,
          py: 1,
          gap: 1,
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={() => hasDetails && setExpanded((e) => !e)}
      >
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
          {new Date(log.created).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 12 }} noWrap>{log.organization_name}</Typography>
        <Typography variant="body2" sx={{ fontSize: 12 }} noWrap>{log.username}</Typography>
        <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace' }} noWrap>{log.tool_name}</Typography>
        <Chip
          label={log.http_method}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600, fontSize: 11, height: 22, fontFamily: 'monospace',
            borderColor: `${methodColor}33`, color: methodColor,
          }}
        />
        <Typography variant="body2" sx={{ fontSize: 12 }}>{log.response_time_ms != null ? `${log.response_time_ms}ms` : '-'}</Typography>
        <Chip
          label={log.is_success ? 'OK' : 'Error'}
          size="small"
          sx={{
            fontWeight: 600, fontSize: 11, height: 22, border: 'none',
            bgcolor: log.is_success ? xxllncColor.opGreen10 : xxllncColor.opRed10,
            color: log.is_success ? xxllncColor.green100 : xxllncColor.red100,
          }}
        />
        {hasDetails ? (
          <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
        ) : <Box />}
      </Box>
      {expanded && <LogDetailPanel log={log} />}
    </Box>
  )
}

const LogsTab: FC = () => {
  const record = useRecordContext<McpServerOut>()
  const translate = useTranslate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const PER_PAGE = 25

  const { data, isLoading } = useGetList<McpUsageLogOut>(
    'mcp_usage_log',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'created', order: 'DESC' },
      // eslint-disable-next-line camelcase
      filter: { server_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const filtered = (data ?? []).filter((row) => {
    if (!search) return true
    return matchesSearch([row.organization_name, row.username, row.tool_name, row.http_method, row.error_message], search.toLowerCase())
  })

  const totalPages = Math.ceil(filtered.length / PER_PAGE)
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  return (
    <>
      <LogsChart data={data} isLoading={isLoading} />
      <SearchField value={search} onChange={setSearch} placeholder={translate('data.searchLogs')} sx={{ mb: 2, display: 'block' }} />
      {isLoading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr 1fr 1fr 80px 70px 60px 32px',
              px: 2,
              py: 1,
              gap: 1,
              bgcolor: 'rgba(0,0,0,0.03)',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            {[
              translate('logs.time'), translate('data.organization'), translate('data.username'),
              translate('data.toolName'), translate('data.httpMethod'), 'ms',
              translate('data.success'), '',
            ].map((h) => (
              <Typography key={h || 'expand'} variant="caption" fontWeight={600} color="text.secondary">{h}</Typography>
            ))}
          </Box>
          {paged.map((log) => (
            <LogRow key={log.id} log={log} />
          ))}
          {filtered.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">{translate('logs.noLogs')}</Typography>
            </Box>
          )}
        </Box>
      )}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <MuiPagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </>
  )
}

const SkillsTab: FC = () => {
  const record = useRecordContext<McpServerOut>()
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()
  const dataProvider = useDataProvider()

  const [linkedSkills, setLinkedSkills] = useState<McpServerSkillLink[]>([])
  const [linkedLoading, setLinkedLoading] = useState(true)

  const { data: allSkills } = useGetList<McpSkillOut>(
    'mcp_skill',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: !!record?.id }
  )

  const { data: applications } = useGetList<ApplicationOut>(
    'application',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'display_name', order: 'ASC' },
    },
    { enabled: !!record?.id }
  )

  // eslint-disable-next-line camelcase
  const [editSkill, setEditSkill] = useState<{
    id?: string; name: string; description: string; content: string;
    application_id?: string | null; // eslint-disable-line camelcase
  } | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [skillAppFilter, setSkillAppFilter] = useState<string | null>(record?.application_id ?? null)

  const fetchLinked = async () => {
    if (!record?.id) return
    setLinkedLoading(true)
    try {
      const { json } = await httpClient(`${getUrl()}/mcp_server/${record.id}/skills`)
      setLinkedSkills(json as McpServerSkillLink[])
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    }
    setLinkedLoading(false)
  }

  useEffect(() => { void fetchLinked() }, [record?.id])

  const linkedSkillIds = new Set(linkedSkills.map((ls) => ls.skill_id))
  const unlinkableSkills = (allSkills ?? [])
    .filter((s) => !linkedSkillIds.has(s.id))
    .filter((s) => !skillAppFilter || String(s.application_id ?? '') === skillAppFilter)

  const handleLink = async (skillId: string) => {
    if (!record?.id) return
    try {
      await postJson(`${getUrl()}/mcp_server_skill`, {
        // eslint-disable-next-line camelcase
        server_id: record.id,
        // eslint-disable-next-line camelcase
        skill_id: skillId,
      })
      notify(translate('data.skillLinked'), { type: 'success' })
      void fetchLinked()
      refresh()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    }
  }

  const handleUnlink = async (linkId: string) => {
    try {
      await httpClient(`${getUrl()}/mcp_server_skill/${linkId}`, { method: 'DELETE' })
      notify(translate('data.skillUnlinked'), { type: 'info' })
      void fetchLinked()
      refresh()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    }
  }

  const handleNewSkill = () => {
    // eslint-disable-next-line camelcase
    setEditSkill({ name: '', description: '', content: '', application_id: record?.application_id ?? null })
    setEditDialogOpen(true)
  }

  const handleEditSkill = (skill: McpServerSkillLink) => {
    setEditSkill({ id: skill.skill_id, name: skill.skill_name, description: '', content: '' })
    void dataProvider.getOne<McpSkillOut>('mcp_skill', { id: skill.skill_id }).then(({ data }) => {
      setEditSkill({
        id: String(data.id), name: data.name, description: data.description ?? '',
        // eslint-disable-next-line camelcase
        content: data.content, application_id: (data.application_id as string | null) ?? null,
      })
    })
    setEditDialogOpen(true)
  }

  const handleSaveSkill = async () => {
    if (!editSkill || !record?.id) return
    setSaving(true)
    try {
      if (editSkill.id) {
        await dataProvider.update('mcp_skill', {
          id: editSkill.id,
          data: {
            name: editSkill.name, description: editSkill.description || null,
            // eslint-disable-next-line camelcase
            content: editSkill.content, application_id: editSkill.application_id || null,
          },
          previousData: editSkill,
        })
        notify(translate('data.skillUpdated'), { type: 'success' })
      } else {
        const { data: created } = await dataProvider.create<McpSkillOut>('mcp_skill', {
          data: {
            name: editSkill.name, description: editSkill.description || null,
            // eslint-disable-next-line camelcase
            content: editSkill.content, application_id: editSkill.application_id || null,
          },
        })
        await postJson(`${getUrl()}/mcp_server_skill`, {
          // eslint-disable-next-line camelcase
          server_id: record.id,
          // eslint-disable-next-line camelcase
          skill_id: String(created.id),
        })
        notify(translate('data.skillCreatedLinked'), { type: 'success' })
      }
      setEditDialogOpen(false)
      setEditSkill(null)
      void fetchLinked()
      refresh()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {translate('data.skillsShared')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <MuiButton variant="outlined" startIcon={<LinkIcon />} onClick={() => setLinkDialogOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            disabled={unlinkableSkills.length === 0}>
            {translate('data.skillLinkExisting')}
          </MuiButton>
          <MuiButton variant="outlined" startIcon={<AddIcon />} onClick={handleNewSkill}
            sx={{ textTransform: 'none', fontWeight: 600, borderStyle: 'dashed' }}>
            {translate('data.skillNew')}
          </MuiButton>
        </Box>
      </Box>

      {linkedSkills.length === 0 && !linkedLoading ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {translate('data.skillsEmpty')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {linkedSkills.map((link) => (
            <Card key={link.id} variant="outlined" sx={{ borderRadius: '12px', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{link.skill_name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <MuiButton
                    size="small"
                    onClick={() => handleEditSkill(link)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: 12,
                      minWidth: 0,
                    }}
                  >
                    {translate('data.skillEdit')}
                  </MuiButton>
                  <MuiButton
                    size="small"
                    color="error"
                    onClick={() => void handleUnlink(link.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: 12,
                      minWidth: 0,
                    }}
                  >
                    {translate('data.skillUnlink')}
                  </MuiButton>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{ onEnter: () => setSkillAppFilter(record?.application_id ?? null) }}
      >
        <DialogTitle>{translate('data.skillLinkExisting')}</DialogTitle>
        <DialogContent>
          {record?.application_id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                label={skillAppFilter
                  ? `${record.application_display_name}`
                  : translate('data.skillFilterAllApps')
                }
                size="small"
                variant={skillAppFilter ? 'filled' : 'outlined'}
                color={skillAppFilter ? 'primary' : 'default'}
                onClick={() => setSkillAppFilter(skillAppFilter ? null : record.application_id!)}
                onDelete={skillAppFilter ? () => setSkillAppFilter(null) : undefined}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          )}
          {unlinkableSkills.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {skillAppFilter
                ? translate('data.skillsAllLinkedForApp')
                : translate('data.skillsAllLinked')
              }
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {unlinkableSkills.map((skill) => (
                <Box key={skill.id} sx={{
                  'display': 'flex', 'alignItems': 'center', 'justifyContent': 'space-between',
                  'p': 1.5, 'borderRadius': '8px', 'border': '1px solid rgba(0,0,0,0.06)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{skill.name}</Typography>
                    {skill.description && (
                      <Typography variant="caption" color="text.secondary">{skill.description}</Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {translate('data.skillLinkedTo', { count: skill.server_count })}
                      </Typography>
                      {skill.application_display_name ? (
                        <Chip label={String(skill.application_display_name)} size="small" variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }} />
                      ) : null}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setLinkDialogOpen(false)
                        handleEditSkill({
                          skill_id: skill.id,
                          skill_name: skill.name,
                          id: '',
                          server_id: '',
                          server_display_name: '',
                          created: '',
                        })
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <EditNoteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <MuiButton
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        void handleLink(skill.id)
                        setLinkDialogOpen(false)
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {translate('data.skillAdd')}
                    </MuiButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setLinkDialogOpen(false)}>{translate('data.skillClose')}</MuiButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editSkill?.id ? translate('data.skillDialogEdit') : translate('data.skillDialogNew')}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '8px !important',
          }}
        >
          <MuiTextField
            label={translate('data.skillName')}
            value={editSkill?.name || ''}
            onChange={(e) =>
              setEditSkill((s) => (s ? { ...s, name: e.target.value } : s))
            }
            fullWidth
            required
            size="small"
          />
          <MuiTextField
            label={translate('data.skillDescription')}
            value={editSkill?.description || ''}
            onChange={(e) =>
              setEditSkill((s) =>
                s ? { ...s, description: e.target.value } : s
              )
            }
            fullWidth
            size="small"
          />
          <MuiTextField
            select
            label={translate('data.skillApplication')}
            value={editSkill?.application_id || ''}
            onChange={(e) =>
              setEditSkill((s) =>
                // eslint-disable-next-line camelcase
                s ? { ...s, application_id: e.target.value || null } : s
              )
            }
            fullWidth
            size="small"
          >
            <MenuItem value="">{translate('data.skillNoApplication')}</MenuItem>
            {(applications ?? []).map((app) => (
              <MenuItem key={app.id} value={app.id}>{app.display_name}</MenuItem>
            ))}
          </MuiTextField>
          <MuiTextField
            label={translate('data.skillContent')}
            value={editSkill?.content || ''}
            onChange={(e) =>
              setEditSkill((s) =>
                s ? { ...s, content: e.target.value } : s
              )
            }
            fullWidth
            required
            multiline
            rows={12}
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setEditDialogOpen(false)}>
            {translate('data.skillCancel')}
          </MuiButton>
          <MuiButton
            onClick={() => void handleSaveSkill()}
            disabled={
              !editSkill?.name || !editSkill?.content || saving
            }
            variant="contained"
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : editSkill?.id ? (
              translate('data.skillSave')
            ) : (
              translate('data.skillCreate')
            )}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

const LinksTab: FC = () => {
  const record = useRecordContext<McpServerOut>()
  if (!record?.id) return null

  return (
    <McpLinksDatagrid
      // eslint-disable-next-line camelcase
      filter={{ server_id: record.id }}
      // eslint-disable-next-line camelcase
      createState={{ server_id: record.id }}
      showOrganization
    />
  )
}

const McpDetailsTab: FC = () => {
  const record = useRecordContext<McpServerOut>()
  const translate = useTranslate()

  const { total: toolCount } = useGetList<McpToolOut>(
    'mcp_tool',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { server_id: record?.id } },
    { enabled: !!record?.id },
  )
  const { total: linkCount } = useGetList<OrganizationMcpLinkOut>(
    'organization_mcp_link',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { server_id: record?.id } },
    { enabled: !!record?.id },
  )
  const { total: skillCount } = useGetList<McpServerSkillOut>(
    'mcp_server_skill',
    // eslint-disable-next-line camelcase
    { pagination: { page: 1, perPage: 1 }, filter: { server_id: record?.id } },
    { enabled: !!record?.id },
  )

  if (!record) return null

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3, animation: `${fadeSlideIn} 0.4s ease-out both` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: '#1a1a1a' }}>
            {record.display_name}
          </Typography>
          <StatusChip active={record.enabled} />
          <ToolSourceChip source={record.tool_source} />
          {record.marketplace && (
            <Chip
              label={translate('data.marketplaceVisible')}
              size="small"
              sx={{
                fontWeight: 600, fontSize: 11, height: 22, border: 'none',
                bgcolor: xxllncColor.opGreen10, color: xxllncColor.green100,
              }}
            />
          )}
        </Box>
        {record.description && (
          <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)', lineHeight: 1.5, maxWidth: 600 }}>
            {record.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
        <StatCard value={toolCount ?? 0} label={translate('data.statTools')} accent={xxllncColor.dark100} delay={80} />
        <StatCard value={linkCount ?? 0} label={translate('data.statLinks')} accent={xxllncColor.orange100} delay={120} />
        <StatCard value={skillCount ?? 0} label={translate('data.statSkills')} accent={xxllncColor.green100} delay={160} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <DetailCard title={translate('data.details')} delay={200}>
          <InfoRow label={translate('data.name')} value={record.name} mono />
          <InfoRow label={translate('data.application')} value={record.application_display_name} />
          <Box sx={{ py: 1.25, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <Typography sx={{
              fontSize: 12, fontWeight: 500, color: 'rgba(0,0,0,0.45)',
              textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1,
            }}>
              {translate('data.connectUrl')}
            </Typography>
            <CopyBlock content={`${getMcpBaseUrl()}/api/v1/mcp/${record.name}/`} />
          </Box>
          <InfoRow label={translate('data.toolSource')} value={record.tool_source} />
          {record.openapi_url && (
            <InfoRow label={translate('data.specUrl')} value={record.openapi_url} mono />
          )}
          <InfoRow label={translate('data.createdAt')} value={formatDateTime(record.created)} />
          <InfoRow label={translate('data.lastModified')} value={formatDateTime(record.lastModified)} />
        </DetailCard>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {record.about && (
            <DetailCard title={translate('data.mcpAbout')} delay={240}>
              <Typography sx={{
                fontSize: 13, color: '#1a1a1a', lineHeight: 1.6,
                whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto',
              }}>
                {record.about}
              </Typography>
            </DetailCard>
          )}
          {record.project_instructions && (
            <DetailCard title={translate('data.projectInstructions')} delay={280}>
              <Typography sx={{
                fontSize: 12, color: '#1a1a1a', lineHeight: 1.6,
                fontFamily: 'monospace', whiteSpace: 'pre-wrap',
                maxHeight: 200, overflow: 'auto',
              }}>
                {record.project_instructions}
              </Typography>
            </DetailCard>
          )}
          {!record.about && !record.project_instructions && (
            <DetailCard title={translate('data.other')} delay={240}>
              <InfoRow label={translate('data.createdAt')} value={formatDateTime(record.created)} />
              <InfoRow label={translate('data.lastModified')} value={formatDateTime(record.lastModified)} />
            </DetailCard>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const McpServerShow: FC = () => {
  const translate = useTranslate()

  return (
    <Show actions={<ShowActions />}>
      <TabbedShowLayout>
        <Tab label="resources.mcp_server.tabs.details">
          <McpDetailsTab />
        </Tab>
        <Tab label="resources.mcp_server.tabs.tools" path="tools">
          <ToolsTab />
        </Tab>
        <Tab label="resources.mcp_server.tabs.skills" path="skills">
          <SkillsTab />
        </Tab>
        <Tab label="resources.mcp_server.tabs.links" path="links">
          <LinksTab />
        </Tab>
        <Tab label="resources.mcp_server.tabs.logs" path="logs">
          <LogsTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default McpServerShow
